import "./index.css";

import { CloseOutlined } from "@ant-design/icons";
import { Button, Empty, Input, Modal, Space, Spin, Tooltip, Tree } from "antd";
import { DataNode } from "antd/es/tree";
import { isEmpty, isNil, map } from "ramda";
import { FC, useEffect, useMemo, useRef } from "react";

import { Icon } from "@/components/icon";

import styles from "./index.module.less";
import { SelectItemType, useStore } from "./use-store";

export interface IPersonnelScopeModalProps {
  open: boolean;
  defaultCheckedNodes?: SelectItemType[];
  onOk: (node: SelectItemType[]) => void;
  onCancel: () => void;
}

export const PersonnelScopeModal: FC<IPersonnelScopeModalProps> = (props) => {
  const { open, onCancel } = props;

  const {
    t,
    tabs,
    activeTab,
    searchValue,
    selectedKeys,
    currentTreeData,
    selectedList,
    treeLoading,
    userTreeExpandedKeys,
    autoExpandSturctureParent,
    foundedKeys,
    selectedPeopleCount,
    setSearchValue,
    handleSearch,
    handleClear,
    handleChangeTab,
    handleCheck,
    handleUserTreeExpand,
    setFoundedKeys,
    handleCancelSelectedKey,
    handleCancel,
    handleOk,
  } = useStore(props);

  // 递归遍历树，构建高亮标题，并收集匹配项的 key
  const loop = (
    data: DataNode[],
    searchValue: string
  ): { tree: DataNode[]; foundedKeys: React.Key[] } => {
    const foundedKeys: React.Key[] = [];

    const traverse = (data: DataNode[]): DataNode[] =>
      data.map((item) => {
        const strTitle = (item.title as string) ?? "";

        const lowerCaseTitle = strTitle.toLowerCase();

        const index = lowerCaseTitle.indexOf(searchValue.toLowerCase());

        if (index > -1) {
          foundedKeys.push(item.key);
        }

        const beforeStr = strTitle.substring(0, index);

        const afterStr = strTitle.slice(index + searchValue.length);

        // 鼠标悬停在节点文本上时显示完整文本
        const title = (
          <Tooltip title={strTitle} mouseEnterDelay={0.3}>
            {index > -1 ? (
              <span>
                {beforeStr}
                <span className="site-tree-search-value">
                  {strTitle.substring(index, index + searchValue.length)}
                </span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            )}
          </Tooltip>
        );

        if (item.children) {
          return {
            ...item,
            title,
            key: item.key,
            children: traverse(item.children),
          };
        }

        return { ...item, title, key: item.key };
      });

    return { tree: traverse(data), foundedKeys };
  };

  // 左边tree数据源，搜索框内容变化自动配高亮显示
  const treeData = useMemo(() => {
    if (isNil(searchValue) || isEmpty(searchValue)) {
      return currentTreeData;
    }
    const { tree, foundedKeys } = loop(currentTreeData, searchValue);

    // 只调用一次 setFoundedKeys
    setFoundedKeys(foundedKeys);

    return tree;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, currentTreeData]);

  // 搜索无结果时显示空状态提示
  const isSearchNotFound = useMemo(
    () =>
      !isNil(searchValue) &&
      !isEmpty(searchValue) &&
      (isNil(foundedKeys) || isEmpty(foundedKeys)),
    [searchValue, foundedKeys]
  );

  // 有搜索结果时，滚动到第一个匹配节点对应的轨道位置
  const treeContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isNil(foundedKeys) && !isEmpty(foundedKeys)) {
      // 等待匹配節點的父節點展開並完成渲染後，再滾動到第一個匹配節點
      const timer = setTimeout(() => {
        // 通過高亮樣式類定位第一個匹配節點，滾動外層容器使其可見
        const firstMatchedNode = treeContainerRef.current?.querySelector(
          ".site-tree-search-value"
        );

        firstMatchedNode?.scrollIntoView({ block: "nearest" });
      });

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foundedKeys]);

  return (
    <Modal
      width={786}
      open={open}
      onCancel={onCancel}
      title={
        <div className="border-0 border-b border-solid border-[#e8e8ef] px-4 py-6 m-0">
          {t("personnel_scope")}
        </div>
      }
      maskClosable={false}
      keyboard={false}
      footer={null}
      className="personnel-scope-modal"
      closable={true}
      zIndex={1001}
    >
      <div className={styles["personnel-scope-modal-body"]}>
        <Input.Search
          rootClassName={styles["searchInput"]}
          allowClear
          onSearch={handleSearch}
          onChange={(e) => {
            if (isEmpty(e.target.value) || isNil(e.target.value)) {
              setFoundedKeys(undefined);
              setSearchValue("");
            }
          }}
        />
        <div className={styles["user-selector-wrapper"]}>
          <div className={styles["user-selector-left"]}>
            <div className={styles["user-selector-header"]}>
              <Space>
                {map((tab) => {
                  return (
                    <span
                      key={tab.key}
                      className={styles["title"]}
                      style={{
                        color: activeTab === tab.key ? "#152751" : "#737489",
                        fontWeight: activeTab === tab.key ? 500 : 400,
                        cursor: activeTab === tab.key ? "pointer" : "default",
                      }}
                      onClick={() => handleChangeTab(tab.key)}
                      data-key={tab.key}
                    >
                      {tab.label}
                    </span>
                  );
                }, tabs)}
              </Space>
            </div>
            <Spin spinning={treeLoading}>
              <div
                ref={treeContainerRef}
                className={styles["user-selector-body"]}
              >
                {isSearchNotFound ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <Empty
                      image={
                        <Icon
                          icon="maskGroupIcon"
                          width={80}
                          style={{ objectFit: "contain" }}
                        />
                      }
                      description={t("no_related_content")}
                    />
                  </div>
                ) : (
                  <Tree
                    style={{
                      width: "max-content",
                    }}
                    checkable
                    checkStrictly
                    selectable={false}
                    showLine={false}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    onCheck={handleCheck}
                    checkedKeys={selectedKeys}
                    treeData={treeData}
                    onExpand={handleUserTreeExpand}
                    expandedKeys={userTreeExpandedKeys}
                    autoExpandParent={autoExpandSturctureParent}
                  />
                )}
              </div>
            </Spin>
          </div>
          <div className={styles["user-selector-right"]}>
            <div className={styles["user-selector-header"]}>
              <span className={styles["title"]}>
                {t("selected")}：{selectedPeopleCount}
              </span>
              <span className={styles["clear-btn"]} onClick={handleClear}>
                {t("clear")}
              </span>
            </div>
            <ul className={styles["user-selector-body"]}>
              {selectedList.map((item) => {
                return (
                  <li key={item.key} className={styles["user-selected-item"]}>
                    <span className={styles["user-name"]}>{item.name}</span>
                    <CloseOutlined
                      onClick={() => handleCancelSelectedKey(item)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      <div className={styles["modal-footer"]}>
        <Button className={styles["cancel-btn"]} onClick={handleCancel}>
          {t("cancel")}
        </Button>
        <Button className={styles["ok-btn"]} onClick={handleOk}>
          {t("confirm")}
        </Button>
      </div>
    </Modal>
  );
};
